import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Facebook, RefreshCw, Loader2, Clock, MapPin, Filter, EyeOff, Calendar, X, Globe, Check, Pencil } from "lucide-react";
import { JatakImageMaker } from "@/components/JatakImageMaker";
import { JatakSchedule } from "@/components/JatakSchedule";
import { JatakSnippets } from "@/components/JatakSnippets";

const fmt = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString("da-DK", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

const dayKey = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch { return ""; }
};

export function JatakFeed() {
  const [data, setData] = useState({ configured: false, posts: [], last_sync: null });
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState("all");
  const [jatakOnly, setJatakOnly] = useState(true);
  const [day, setDay] = useState("");
  const [hiding, setHiding] = useState(null);
  const [pubBusy, setPubBusy] = useState(null);
  const [linkEdit, setLinkEdit] = useState(null);
  const [linkVal, setLinkVal] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/jatak/facebook/posts"); setData(data); }
    catch { toast.error("Kunne ikke hente opslag"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try { await api.post("/jatak/facebook/sync"); await load(); toast.success("Opslag opdateret fra Facebook"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke opdatere"); }
    finally { setSyncing(false); }
  };

  const hide = async (p) => {
    setHiding(p.id);
    try {
      await api.post(`/jatak/facebook/posts/${p.id}/hide`);
      setData((d) => ({ ...d, posts: d.posts.filter((x) => x.id !== p.id) }));
      toast.success("Opslag skjult");
    } catch { toast.error("Kunne ikke skjule opslaget"); }
    finally { setHiding(null); }
  };

  const setPublic = async (p) => {
    setPubBusy(p.id);
    try {
      const next = !p.public;
      await api.post(`/jatak/facebook/posts/${p.id}/public`, { public: next });
      setData((d) => ({ ...d, posts: d.posts.map((x) => (x.id === p.id ? { ...x, public: next } : x)) }));
      toast.success(next ? "Vist på tilbudssiden" : "Fjernet fra tilbudssiden");
    } catch { toast.error("Kunne ikke opdatere"); }
    finally { setPubBusy(null); }
  };

  const startEditLink = (p) => { setLinkEdit(p.id); setLinkVal(p.public_link || p.permalink_url || ""); };

  const saveLink = async (p) => {
    setLinkBusy(true);
    try {
      const link = linkVal.trim();
      const { data } = await api.post(`/jatak/facebook/posts/${p.id}/link`, { link });
      setData((d) => ({ ...d, posts: d.posts.map((x) => (x.id === p.id ? { ...x, public_link: data.public_link } : x)) }));
      setLinkEdit(null);
      toast.success(link ? "Facebook-link gemt" : "Link fjernet (bruger automatisk link)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke gemme linket"); }
    finally { setLinkBusy(false); }
  };

  const stores = Array.from(new Set(data.posts.map((p) => p.store).filter(Boolean)));
  const posts = data.posts.filter((p) =>
    (store === "all" || p.store === store) &&
    (!jatakOnly || p.is_jatak) &&
    (!day || dayKey(p.created_time) === day)
  );

  return (
    <div className="space-y-4" data-testid="jatakfeed">
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="jatakfeed-header">
        <Facebook className="w-4 h-4 text-[#1877F2]" />
        <span>Ja tak-opslag fra Facebook · følg med i hvad der er lagt op og hvornår</span>
      </div>

      <JatakSchedule />

      <JatakSnippets />

      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 rounded-lg bg-[#f1f5f9] p-1" data-testid="store-filter">
              {["all", ...stores].map((s) => (
                <button key={s} onClick={() => setStore(s)} data-testid={`store-${s}`}
                  className={`px-3 h-8 rounded-md text-sm font-medium transition-colors ${store === s ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  {s === "all" ? "Alle butikker" : s}
                </button>
              ))}
            </div>
            <button onClick={() => setJatakOnly((v) => !v)} data-testid="jatak-only-toggle"
              className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-sm font-medium border transition-colors ${jatakOnly ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700" : "bg-white border-[#e2e8f0] text-slate-500"}`}>
              <Filter className="w-3.5 h-3.5" /> {jatakOnly ? "Kun Ja tak" : "Alle opslag"}
            </button>
            <div className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-sm font-medium border transition-colors ${day ? "bg-sky-500/10 border-sky-500/30 text-sky-700" : "bg-white border-[#e2e8f0] text-slate-500"}`}>
              <Calendar className="w-3.5 h-3.5" />
              <input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="day-picker"
                className="bg-transparent outline-none text-sm w-[8.5rem]" />
              {day && (
                <button onClick={() => setDay("")} data-testid="day-clear" className="text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data.last_sync && <span className="text-xs text-slate-400">opdateret {fmt(data.last_sync)}</span>}
            <Button onClick={sync} disabled={syncing} variant="outline" className="h-8 rounded-lg gap-1.5 text-xs" data-testid="feed-sync-btn">
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}Opdater
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 py-6 text-center">Henter…</p>
        ) : !data.configured ? (
          <p className="text-sm text-slate-500" data-testid="feed-not-configured">Facebook-feed er ikke koblet på endnu.</p>
        ) : posts.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="feed-empty">Ingen opslag matcher endnu. Tryk "Opdater", eller juster filtrene ovenfor.</p>
        ) : (
          <div className="space-y-3" data-testid="feed-list">
            {posts.map((p) => (
              <div key={p.id} className="flex gap-3 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-3" data-testid="feed-post">
                {p.image && <img src={p.image} alt="" className="w-24 h-24 rounded-lg object-cover shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {p.store && <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-700 border border-sky-500/20"><MapPin className="w-3 h-3" />{p.store}</span>}
                    {(p.jatak_times || []).map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 border border-amber-500/25"><Clock className="w-3 h-3" />{t}</span>
                    ))}
                    <span className="text-xs text-slate-400">{fmt(p.created_time)}</span>
                  </div>
                  {p.message && <p className="text-sm text-slate-700 whitespace-pre-wrap">{p.message}</p>}
                  <div className="mt-1.5">
                    {linkEdit === p.id ? (
                      <div className="flex items-center gap-1.5">
                        <input value={linkVal} onChange={(e) => setLinkVal(e.target.value)} placeholder="https://facebook.com/..." data-testid="link-input"
                          className="flex-1 min-w-0 h-7 px-2 text-xs rounded-md border border-[#e2e8f0] bg-white" />
                        <button onClick={() => saveLink(p)} disabled={linkBusy} data-testid="link-save"
                          className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium bg-emerald-500/10 border border-emerald-500/30 text-emerald-700">
                          {linkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Gem
                        </button>
                        <button onClick={() => setLinkEdit(null)} className="inline-flex items-center h-7 px-2 rounded-md text-xs text-slate-500 border border-[#e2e8f0]"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 flex-wrap">
                        {(p.public_link || p.permalink_url)
                          ? <a href={p.public_link || p.permalink_url} target="_blank" rel="noreferrer" className="text-xs text-[#1877F2] hover:underline inline-flex items-center gap-1">{p.public_link ? "Eget link" : "Åbn på Facebook"}</a>
                          : <span className="text-xs text-slate-400">Intet link</span>}
                        <button onClick={() => startEditLink(p)} data-testid="link-edit" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#C8102E]">
                          <Pencil className="w-3 h-3" />{p.public_link ? "Rediger link" : "Tilføj link"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="self-start shrink-0 flex flex-col gap-1.5">
                  {p.image && (
                    <JatakImageMaker imageUrl={p.image} defaultName={(p.message || "").split("\n")[0].slice(0, 60)} />
                  )}
                  <button onClick={() => setPublic(p)} disabled={pubBusy === p.id} data-testid="feed-public-btn"
                    title={p.public ? "Fjern fra tilbudsside" : "Vis på lagersalgtilbud.dk"}
                    className={`inline-flex items-center justify-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium border transition-colors ${p.public ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700" : "bg-white border-[#e2e8f0] text-slate-500 hover:text-emerald-600 hover:border-emerald-300"}`}>
                    {pubBusy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}{p.public ? "På tilbudsside" : "Vis offentligt"}
                  </button>
                  <button onClick={() => hide(p)} disabled={hiding === p.id} data-testid="feed-hide-btn"
                    title="Skjul opslag" className="inline-flex items-center justify-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium border border-[#e2e8f0] bg-white text-slate-500 hover:text-rose-600 hover:border-rose-300 transition-colors">
                    {hiding === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />}Skjul
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
